import React from 'react'
import type { FloatCard, Slide as SlideModel } from './slides.js'

/** 画布尺寸：440x956 CSS px，导出时用 3x DPR → 1320x2868（App Store 6.9 寸档）。 */
export const CANVAS_W = 440
export const CANVAS_H = 956

const LIGHT_BG = '#F3F1EA'
const DARK_BG = '#0F1417'
const LIGHT_TEXT = '#15181A'
const LIGHT_SUB = '#6A716E'
const ACCENT_ON_LIGHT = '#0B9E8D'
const ACCENT_ON_DARK = '#2FE0CB'

/** 卡片配色取自 App 里活动类型的色板，保证浮出卡片和界面是同一套颜色。 */
const TINTS: Record<FloatCard['tint'], { bg: string; label: string; value: string }> = {
  teal: { bg: '#D6F5EF', label: '#0B7F72', value: '#0A5F56' },
  blue: { bg: '#DCE8FB', label: '#3568B0', value: '#24487D' },
  amber: { bg: '#FBEBD2', label: '#A9752A', value: '#7C5416' },
  green: { bg: '#DDF1DC', label: '#3D7C40', value: '#2B5C2E' },
  plum: { bg: '#E7E1F7', label: '#6A55A8', value: '#4C3B80' },
  dark: { bg: '#1C2326', label: '#9AA5A4', value: '#FFFFFF' },
}

function FloatingCard({ card }: { card: FloatCard }) {
  const tint = TINTS[card.tint]
  return (
    <div
      style={{
        position: 'absolute',
        left: card.x,
        top: card.y,
        background: tint.bg,
        borderRadius: 20,
        padding: '12px 16px 13px',
        transform: `rotate(${card.rotate ?? 0}deg)`,
        boxShadow: '0 18px 36px -14px rgba(16,24,28,0.38)',
        minWidth: 108,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: tint.label, marginBottom: 2 }}>
        {card.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 34, fontWeight: 800, color: tint.value, letterSpacing: -1 }}>
          {card.value}
        </span>
        {card.unit && (
          <span style={{ fontSize: 15, fontWeight: 600, color: tint.label }}>{card.unit}</span>
        )}
      </div>
    </div>
  )
}

export default function Slide({ slide }: { slide: SlideModel }) {
  const dark = !!slide.dark
  const accent = dark ? ACCENT_ON_DARK : ACCENT_ON_LIGHT
  const titleColor = dark ? '#FFFFFF' : LIGHT_TEXT
  const subColor = dark ? 'rgba(255,255,255,0.68)' : LIGHT_SUB

  return (
    <div
      style={{
        position: 'relative',
        width: CANVAS_W,
        height: CANVAS_H,
        overflow: 'hidden',
        background: dark ? DARK_BG : LIGHT_BG,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Helvetica, Arial, sans-serif',
      }}
    >
      {/* 标题区 */}
      <div style={{ position: 'absolute', left: 30, right: 30, top: 62 }}>
        <div
          style={{
            fontSize: 38,
            lineHeight: 1.16,
            fontWeight: 800,
            letterSpacing: -0.6,
            color: titleColor,
          }}
        >
          {slide.title}
        </div>
        {slide.titleAccent && (
          <div
            style={{
              fontSize: 38,
              lineHeight: 1.16,
              fontWeight: 800,
              letterSpacing: -0.6,
              color: accent,
            }}
          >
            {slide.titleAccent}
          </div>
        )}
        {slide.sub && (
          <div
            style={{
              marginTop: 14,
              fontSize: 16.5,
              lineHeight: 1.45,
              fontWeight: 500,
              color: subColor,
              maxWidth: 356,
            }}
          >
            {slide.sub}
          </div>
        )}
      </div>

      {/* imageBleed：图片铺满画布下半部当背景，不套手机框。
          第 6 张用这个 —— 主角是抠出来的回忆卡，地图只是底子。 */}
      {slide.imageBleed && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: slide.offsetY ?? 250,
            bottom: 0,
            overflow: 'hidden',
          }}
        >
          <img
            src={slide.image}
            alt=""
            style={{ width: '100%', display: 'block', marginTop: -(slide.cropTop ?? 0) }}
          />
          {/* 顶部压一层同色渐变，让地图和标题区平滑接上 */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: 120,
              background: `linear-gradient(to bottom, ${dark ? DARK_BG : LIGHT_BG}, transparent)`,
            }}
          />
        </div>
      )}

      {/* 手机：倾斜 + 底部出血，画布 overflow hidden 把多出来的部分切掉。
          cropTop 是往下挪的取景窗 —— 有些截图上半屏是地图或粘顶的标题，切掉才显得满。 */}
      {!slide.imageBleed && (
      <div
        style={{
          position: 'absolute',
          left: slide.offsetX ?? 40,
          top: slide.offsetY ?? 306,
          width: slide.phoneWidth ?? 360,
          // 默认让手机往画布下面出血 140px；但取景窗往下挪之后图就那么高了，
          // 再拉高只会在底下露出一块白 —— 所以取两者的小值。
          height: Math.min(
            CANVAS_H - (slide.offsetY ?? 306) + 140,
            ((slide.phoneWidth ?? 360) * 2868) / 1320 - (slide.cropTop ?? 0),
          ),
          borderRadius: 42,
          overflow: 'hidden',
          transform: `rotate(${slide.rotate ?? -3}deg)`,
          boxShadow: '0 40px 80px -30px rgba(10,20,24,0.55), 0 0 0 1px rgba(0,0,0,0.05)',
          background: '#fff',
        }}
      >
        <img
          src={slide.image}
          alt=""
          style={{ width: '100%', display: 'block', marginTop: -(slide.cropTop ?? 0) }}
        />
      </div>
      )}

      {/* 第 6 张：抠出来的回忆卡，按时间依次叠在地图上 */}
      {slide.stackedCards?.map((card, i) => (
        <img
          key={i}
          src={card.image}
          alt=""
          style={{
            position: 'absolute',
            left: card.x,
            top: card.y,
            width: card.width,
            borderRadius: 18,
            transform: `rotate(${card.rotate ?? 0}deg)`,
            boxShadow: '0 22px 44px -18px rgba(16,24,28,0.45)',
          }}
        />
      ))}

      {slide.cards?.map((card) => <FloatingCard key={card.label} card={card} />)}

      {/* 第 1 张：围在手机周围的功能胶囊，讲「这些数据都汇到一起了」 */}
      {slide.chips?.map((chip) => (
        <div
          key={chip.label}
          style={{
            position: 'absolute',
            left: chip.x,
            top: chip.y,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: dark ? 'rgba(255,255,255,0.10)' : '#FFFFFF',
            border: dark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(20,24,26,0.06)',
            borderRadius: 999,
            padding: '8px 14px 8px 11px',
            transform: `rotate(${chip.rotate ?? 0}deg)`,
            boxShadow: '0 12px 26px -12px rgba(16,24,28,0.35)',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 17 }}>{chip.emoji}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: dark ? '#fff' : LIGHT_TEXT }}>
            {chip.label}
          </span>
        </div>
      ))}

      {/* 第 1 张：真实用户评价，原文截断不改写 */}
      {slide.quote && (
        <div
          style={{
            position: 'absolute',
            left: 26,
            right: 26,
            bottom: 34,
            background: 'rgba(12,17,20,0.86)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 22,
            padding: '18px 20px 16px',
            backdropFilter: 'blur(18px)',
          }}
        >
          <div style={{ fontSize: 17, fontWeight: 600, color: accent, letterSpacing: 1 }}>
            ★★★★★
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 15.5,
              lineHeight: 1.5,
              fontWeight: 500,
              color: 'rgba(255,255,255,0.92)',
            }}
          >
            “{slide.quote.text}”
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            — {slide.quote.source}
          </div>
        </div>
      )}

      {/* 第 8 张：用户量，口径以 ASC 为准。放在标题下方，别被手机压住。 */}
      {slide.footnote && (
        <div
          style={{
            position: 'absolute',
            left: 30,
            top: 176,
            fontSize: 17,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.72)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 999,
            padding: '7px 16px',
          }}
        >
          {slide.footnote}
        </div>
      )}
    </div>
  )
}
